import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSessionTypes } from "../hooks/useSessionTypes";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { ConsentToggleRow } from "../components/ConsentToggleRow";
import { ProfilePhotoUpload } from "../components/ProfilePhotoUpload";
import { toast } from "../components/ui/sonner";

const PAIRING_OPTIONS = [
  { value: "any", label: "Anyone" },
  { value: "same_gender", label: "Same gender" },
  { value: "opposite_gender", label: "Opposite gender" },
  { value: "women_only", label: "Women only" },
  { value: "men_only", label: "Men only" },
];

const INTERESTS = ["Hiking", "Photography", "Music", "Travel", "Coffee", "Art", "Fitness", "Reading", "Food", "Nature"];

const STEPS_LABELS = ["Profile Photo", "About You", "Pairing & Preferences", "Personality", "Comfort & Boundaries", "Final Details"];

const EMPTY_FORM = {
  date_of_birth: "",
  gender: "",
  pairing_comfort: [],
  session_preference: "friendship",
  interests: [],
  personality: "extrovert",
  comfort_level: "",
  photography_experience: "some",
  posing_boundaries: { hand_holding: false, hugging: false, sitting_close: false, arm_around_shoulder: false, other: false },
  accessibility_notes: "",
  emergency_contact_name: "",
  emergency_contact_phone: "",
  expectations_notes: "",
};

export default function StrangerRegister() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const sessionTypes = useSessionTypes();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const togglePairing = (value) => {
    setForm((f) => ({ ...f, pairing_comfort: f.pairing_comfort.includes(value) ? f.pairing_comfort.filter((v) => v !== value) : [...f.pairing_comfort, value] }));
  };
  const toggleInterest = (value) => {
    setForm((f) => ({ ...f, interests: f.interests.includes(value) ? f.interests.filter((v) => v !== value) : [...f.interests, value] }));
  };

  const next = () => {
    if (step === 0 && !user?.profile_photo_url) {
      toast.error("Please upload a profile photo to continue.");
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS_LABELS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.put("/auth/stranger-profile", form);
      setUser(data);
      toast.success("Your Stranger profile is ready.");
      navigate("/find-a-session");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-5 sm:px-8 py-16" data-testid="stranger-register-page">
      <p className="text-stranger-orange text-xs font-bold tracking-[0.25em] uppercase mb-2">
        Step {step + 1} of {STEPS_LABELS.length}
      </p>
      <h1 className="font-extrabold uppercase text-2xl sm:text-3xl tracking-tight mb-8">{STEPS_LABELS[step]}</h1>

      <div className="w-full h-1.5 bg-soft-gray rounded-full mb-10 overflow-hidden">
        <div className="h-full bg-stranger-orange transition-all duration-300" style={{ width: `${((step + 1) / STEPS_LABELS.length) * 100}%` }} data-testid="register-progress-bar" />
      </div>

      {step === 0 && (
        <div className="space-y-5 text-center" data-testid="register-step-photo">
          <p className="text-sm font-medium text-midnight-black/60 max-w-sm mx-auto -mt-4 mb-6">
            For everyone's safety, we require a clear, recent photo of your face. It's shared only with your host photographer and our admin team for verification — never with other Strangers, including your match.
          </p>
          <ProfilePhotoUpload user={user} onUploaded={(data) => setUser((u) => ({ ...u, ...data }))} />
        </div>
      )}

      {step === 1 && (
        <div className="space-y-5" data-testid="register-step-about">
          <div>
            <label className="text-sm font-bold block mb-2">Date of birth</label>
            <Input data-testid="register-dob-input" type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} />
          </div>
          <div>
            <label className="text-sm font-bold block mb-2">Gender</label>
            <div className="grid grid-cols-3 gap-3">
              {["woman", "man", "non-binary"].map((g) => (
                <button
                  key={g}
                  type="button"
                  data-testid={`register-gender-${g}`}
                  onClick={() => setForm({ ...form, gender: g })}
                  className={`py-3 rounded-xl border-2 font-bold text-xs uppercase ${form.gender === g ? "border-stranger-orange bg-warm-white" : "border-soft-gray"}`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-6" data-testid="register-step-pairing">
          <div>
            <label className="text-sm font-bold block mb-3">Who are you comfortable being paired with?</label>
            <div className="flex flex-wrap gap-2">
              {PAIRING_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  data-testid={`register-pairing-${o.value}`}
                  onClick={() => togglePairing(o.value)}
                  className={`px-4 py-2 rounded-full border-2 text-xs font-bold uppercase ${form.pairing_comfort.includes(o.value) ? "border-stranger-orange bg-warm-white" : "border-soft-gray"}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm font-bold block mb-3">What kind of connection are you open to?</label>
            <div className="grid grid-cols-3 gap-3">
              {sessionTypes.map((t) => (
                <button
                  key={t.type_key}
                  type="button"
                  data-testid={`register-preference-${t.type_key}`}
                  onClick={() => setForm({ ...form, session_preference: t.type_key })}
                  className={`py-3 rounded-xl border-2 font-bold text-xs uppercase ${form.session_preference === t.type_key ? "border-stranger-orange bg-warm-white" : "border-soft-gray"}`}
                >
                  {t.type_key}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-6" data-testid="register-step-personality">
          <div>
            <label className="text-sm font-bold block mb-3">Are you more introvert or extrovert?</label>
            <div className="grid grid-cols-3 gap-3">
              {["introvert", "balanced", "extrovert"].map((p) => (
                <button
                  key={p}
                  type="button"
                  data-testid={`register-personality-${p}`}
                  onClick={() => setForm({ ...form, personality: p })}
                  className={`py-3 rounded-xl border-2 font-bold text-xs uppercase ${form.personality === p ? "border-stranger-orange bg-warm-white" : "border-soft-gray"}`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm font-bold block mb-3">Interests</label>
            <div className="flex flex-wrap gap-2">
              {INTERESTS.map((i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`register-interest-${i.toLowerCase()}`}
                  onClick={() => toggleInterest(i)}
                  className={`px-4 py-2 rounded-full border-2 text-xs font-bold uppercase ${form.interests.includes(i) ? "border-stranger-orange bg-warm-white" : "border-soft-gray"}`}
                >
                  {i}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm font-bold block mb-2">Photography experience</label>
            <div className="grid grid-cols-3 gap-3">
              {["none", "some", "a-lot"].map((p) => (
                <button
                  key={p}
                  type="button"
                  data-testid={`register-photo-experience-${p}`}
                  onClick={() => setForm({ ...form, photography_experience: p })}
                  className={`py-3 rounded-xl border-2 font-bold text-xs uppercase ${form.photography_experience === p ? "border-stranger-orange bg-warm-white" : "border-soft-gray"}`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {step === 4 && (
        <div data-testid="register-step-boundaries">
          <p className="text-sm font-medium text-midnight-black/60 mb-4">
            Nothing here is assumed. These are your defaults — you can always adjust them per session, and withdraw consent at any time.
          </p>
          <ConsentToggleRow
            testId="register-boundary-hand-holding"
            label="Hand holding"
            checked={form.posing_boundaries.hand_holding}
            onChange={(v) => setForm({ ...form, posing_boundaries: { ...form.posing_boundaries, hand_holding: v } })}
          />
          <ConsentToggleRow
            testId="register-boundary-hugging"
            label="Hugging"
            checked={form.posing_boundaries.hugging}
            onChange={(v) => setForm({ ...form, posing_boundaries: { ...form.posing_boundaries, hugging: v } })}
          />
          <ConsentToggleRow
            testId="register-boundary-sitting-close"
            label="Sitting close"
            checked={form.posing_boundaries.sitting_close}
            onChange={(v) => setForm({ ...form, posing_boundaries: { ...form.posing_boundaries, sitting_close: v } })}
          />
          <ConsentToggleRow
            testId="register-boundary-arm-around-shoulder"
            label="Arm around shoulder"
            checked={form.posing_boundaries.arm_around_shoulder}
            onChange={(v) => setForm({ ...form, posing_boundaries: { ...form.posing_boundaries, arm_around_shoulder: v } })}
          />
          <div className="mt-6">
            <label className="text-sm font-bold block mb-2">Accessibility considerations (optional)</label>
            <Textarea data-testid="register-accessibility-input" placeholder="Let us know if you need any accommodations" value={form.accessibility_notes} onChange={(e) => setForm({ ...form, accessibility_notes: e.target.value })} />
          </div>
        </div>
      )}

      {step === 5 && (
        <div className="space-y-5" data-testid="register-step-final">
          <div>
            <label className="text-sm font-bold block mb-2">Overall comfort level</label>
            <Textarea data-testid="register-comfort-level-input" placeholder="How are you feeling about meeting a Stranger?" value={form.comfort_level} onChange={(e) => setForm({ ...form, comfort_level: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input data-testid="register-emergency-name-input" placeholder="Emergency contact name" value={form.emergency_contact_name} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} />
            <Input data-testid="register-emergency-phone-input" placeholder="Emergency contact phone" value={form.emergency_contact_phone} onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })} />
          </div>
          <div>
            <label className="text-sm font-bold block mb-2">What are you hoping to get out of this?</label>
            <Textarea data-testid="register-expectations-input" placeholder="Session expectations" value={form.expectations_notes} onChange={(e) => setForm({ ...form, expectations_notes: e.target.value })} />
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mt-10">
        <Button type="button" variant="outline" data-testid="register-back-button" onClick={back} disabled={step === 0} className="rounded-full px-6 font-bold">
          BACK
        </Button>
        {step < STEPS_LABELS.length - 1 ? (
          <Button type="button" data-testid="register-next-button" onClick={next} className="bg-stranger-orange hover:bg-orange-600 text-clean-white font-bold rounded-full px-8">
            NEXT
          </Button>
        ) : (
          <Button type="button" data-testid="register-submit-button" onClick={submit} disabled={submitting} className="bg-stranger-orange hover:bg-orange-600 text-clean-white font-bold rounded-full px-8">
            {submitting ? "Saving..." : "COMPLETE PROFILE"}
          </Button>
        )}
      </div>
    </div>
  );
}
